import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { api } from '../lib/api.js';

// Password, MFA and passkeys are managed centrally at the CXFOP identity
// platform — the app keeps only the local display name. Profile fields
// cached from the platform (email, CathayID, roster name) are read-only
// here; corrections go through the central directory.
export default function Profile() {
  const { user, refresh } = useAuth();
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-cathay-700">My account</h1>
        <p className="text-sm text-slate-600 mt-1">Signed in as {user?.email}</p>
      </header>
      <ProfilePanel user={user} refresh={refresh} />
      <section className="bg-white rounded shadow-sm border p-4 max-w-lg text-sm text-slate-700 space-y-2">
        <h2 className="font-medium text-slate-800">Sign-in &amp; security</h2>
        <p>
          Your password and multi-factor authentication are managed by the CXFOP identity
          platform, not this app.
        </p>
        <a
          href="https://login.cxfop.com/if/user/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-cathay-700 hover:underline"
        >
          Manage your CXFOP account →
        </a>
      </section>
    </div>
  );
}

function ProfilePanel({ user, refresh }) {
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setErr(null);
    setOk(null);
    setBusy(true);
    try {
      await api.auth.updateMe({ display_name: displayName.trim() });
      setOk('Saved.');
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white rounded shadow-sm border p-4 space-y-4 max-w-lg">
      <label className="block">
        <span className="text-sm text-slate-700">Display name</span>
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
        />
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <ReadOnlyField label="Email (from CXFOP)" value={user?.email} mono />
        <ReadOnlyField label="CathayID (from CXFOP)" value={user?.cathay_id} mono />
        <ReadOnlyField label="Roster name (from CXFOP)" value={user?.roster_name} />
        <ReadOnlyField label="Role" value={user?.role} />
      </div>
      {err && <div className="text-sm text-red-600">{err}</div>}
      {ok && <div className="text-sm text-green-700">{ok}</div>}
      <button
        disabled={busy}
        className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
      >
        Save
      </button>
    </form>
  );
}

function ReadOnlyField({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={'mt-1 px-2 py-1.5 bg-slate-50 rounded border border-slate-200 ' + (mono ? 'font-mono' : '')}>
        {value || <span className="text-slate-400">—</span>}
      </div>
    </div>
  );
}
